import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { fmtH } from "../lib/planning";
import {
    Clock, MapPin, Loader2, LogIn, LogOut, Pencil, Trash2, Plus, FileDown,
    ChevronLeft, ChevronRight, AlertTriangle, RotateCcw, Radio, CalendarDays,
    UserCheck, Search, ShieldCheck,
} from "lucide-react";

const ALL = "__all__";
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const toLocalInput = (iso) => {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtOre = fmtH;
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const SHIFT_ANOMALY_LABELS = {
    entrata_ritardo: "ENTRATA IN RITARDO",
    uscita_anticipata: "USCITA ANTICIPATA",
    uscita_posticipata: "USCITA OLTRE TURNO",
};

export default function PresenzePage() {
    const { user } = useAuth();
    const [enabled, setEnabled] = useState(null);
    const [worksites, setWorksites] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [showAbilitazioni, setShowAbilitazioni] = useState(false);

    useEffect(() => {
        api.get("/presenze/config").then((r) => setEnabled(r.data.enabled)).catch(() => setEnabled(false));
        api.get("/presenze/worksites").then((r) => setWorksites(r.data)).catch(() => {});
        api.get("/employees").then((r) => {
            setEmployees([...r.data].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`)));
        }).catch(() => {});
    }, []);

    const downloadPrivacyPdf = async (path, filename) => {
        try {
            const { data: blob } = await api.get(path, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([blob]));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Errore nel download del PDF");
        }
    };

    const toggleModule = async (val) => {
        try {
            await api.post("/presenze/config", { enabled: val });
            setEnabled(val);
            toast.success(val ? "Timbratura ATTIVATA per i dipendenti" : "Timbratura disattivata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nel salvataggio");
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="presenze-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Clock className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Presenze</h1>
                        <p className="text-sm text-slate-500">Timbrature geolocalizzate, foglio ore e anomalie</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Link to="/presenze/cantieri">
                        <Button variant="outline" className="gap-2" data-testid="presenze-cantieri-link">
                            <MapPin className="h-4 w-4" /> Gestisci cantieri
                        </Button>
                    </Link>
                    {user?.role === "admin" && (
                        <Button variant="outline" className="gap-2" onClick={() => setShowAbilitazioni(true)} data-testid="abilitazioni-open-btn">
                            <UserCheck className="h-4 w-4" /> Chi può timbrare
                        </Button>
                    )}
                    {user?.role === "admin" && (
                        <>
                            <Button
                                variant="outline" className="gap-2"
                                onClick={() => downloadPrivacyPdf("/presenze/geo-informativa-pdf", "Informativa_Geolocalizzazione_Timbratura.pdf")}
                                data-testid="geo-informativa-download-btn"
                            >
                                <ShieldCheck className="h-4 w-4" /> Informativa geo (PDF)
                            </Button>
                            <Button
                                variant="outline" className="gap-2"
                                onClick={() => downloadPrivacyPdf("/presenze/dpia-draft", "DPIA_Timbratura_Geolocalizzata_Bozza.pdf")}
                                data-testid="dpia-download-btn"
                            >
                                <FileDown className="h-4 w-4" /> Bozza DPIA (PDF)
                            </Button>
                        </>
                    )}
                    {user?.role === "admin" && enabled !== null && (
                        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${enabled ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="presenze-module-toggle">
                            <span className={`text-xs font-bold ${enabled ? "text-emerald-700" : "text-amber-700"}`}>
                                Timbratura dipendenti: {enabled ? "ATTIVA" : "DISATTIVATA"}
                            </span>
                            <Switch checked={enabled} onCheckedChange={toggleModule} data-testid="presenze-enable-switch" />
                        </div>
                    )}
                </div>
            </div>

            <Tabs defaultValue="live">
                <TabsList className="mb-4">
                    <TabsTrigger value="live" className="gap-1.5" data-testid="presenze-tab-live"><Radio className="h-3.5 w-3.5" /> In corso</TabsTrigger>
                    <TabsTrigger value="entries" className="gap-1.5" data-testid="presenze-tab-entries"><Clock className="h-3.5 w-3.5" /> Timbrature</TabsTrigger>
                    <TabsTrigger value="timesheet" className="gap-1.5" data-testid="presenze-tab-timesheet"><CalendarDays className="h-3.5 w-3.5" /> Foglio ore</TabsTrigger>
                </TabsList>
                <TabsContent value="live"><LiveTab /></TabsContent>
                <TabsContent value="entries"><EntriesTab worksites={worksites} employees={employees} /></TabsContent>
                <TabsContent value="timesheet"><TimesheetTab /></TabsContent>
            </Tabs>
            <AbilitazioniDialog open={showAbilitazioni} onClose={() => setShowAbilitazioni(false)} />
        </div>
    );
}

function AbilitazioniDialog({ open, onClose }) {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [groupId, setGroupId] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);

    const load = useCallback(() => {
        api.get("/presenze/abilitazioni")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore nel caricamento delle abilitazioni"));
    }, []);

    useEffect(() => {
        if (open) { setQ(""); setGroupId(""); setData(null); load(); }
    }, [open, load]);

    const filtered = useMemo(() => {
        if (!data) return [];
        const needle = q.trim().toLowerCase();
        return data.employees.filter((e) =>
            (!groupId || (e.group_ids || []).includes(groupId)) &&
            (!needle || `${e.cognome} ${e.nome} ${e.matricola || ""}`.toLowerCase().includes(needle)));
    }, [data, q, groupId]);

    const enabledCount = data ? data.employees.filter((e) => e.enabled).length : 0;

    const toggleOne = async (emp) => {
        const val = !emp.enabled;
        setData((prev) => ({ ...prev, employees: prev.employees.map((e) => (e.id === emp.id ? { ...e, enabled: val } : e)) }));
        try {
            await api.post("/presenze/abilitazioni", { employee_ids: [emp.id], enabled: val });
        } catch {
            toast.error("Errore nel salvataggio");
            load();
        }
    };

    const bulkGroup = async (enable) => {
        if (!groupId) return;
        setBulkSaving(true);
        try {
            const { data: res } = await api.post("/presenze/abilitazioni", { group_id: groupId, enabled: enable });
            toast.success(`${res.requested} dipendenti del gruppo ${enable ? "abilitati alla timbratura" : "disabilitati"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nel salvataggio");
        } finally { setBulkSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[88vh] w-[calc(100vw-2rem)] max-w-xl overflow-hidden" data-testid="abilitazioni-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Chi può timbrare</DialogTitle>
                    <DialogDescription>
                        Solo i dipendenti abilitati possono usare la timbratura. Chi non è abilitato vede il modulo come disattivato.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={groupId} onChange={(e) => setGroupId(e.target.value)}
                        className="h-9 flex-1 min-w-[150px] rounded-md border border-slate-200 bg-white px-2 text-sm"
                        data-testid="abilitazioni-group-select"
                    >
                        <option value="">Tutti i gruppi</option>
                        {(data?.groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        disabled={!groupId || bulkSaving} onClick={() => bulkGroup(true)} data-testid="abilitazioni-bulk-on">
                        {bulkSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Abilita gruppo
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        disabled={!groupId || bulkSaving} onClick={() => bulkGroup(false)} data-testid="abilitazioni-bulk-off">
                        Disabilita gruppo
                    </Button>
                </div>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca dipendente…"
                        className="h-9 w-full rounded-md border border-slate-200 pl-8 pr-3 text-sm"
                        data-testid="abilitazioni-search"
                    />
                </div>
                <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200">
                    {!data ? (
                        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">Nessun dipendente trovato.</div>
                    ) : (
                        filtered.map((e) => (
                            <div key={e.id} className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`abilitazione-row-${e.id}`}>
                                <div>
                                    <span className="font-medium text-slate-700">{e.cognome} {e.nome}</span>
                                    {e.matricola && <span className="ml-1.5 text-[11px] text-slate-400">matr. {e.matricola}</span>}
                                </div>
                                <Switch checked={e.enabled} onCheckedChange={() => toggleOne(e)} data-testid={`abilitazione-switch-${e.id}`} />
                            </div>
                        ))
                    )}
                </div>
                <div className="flex items-center justify-between pt-1">
                    <span className="text-xs font-semibold text-slate-500" data-testid="abilitazioni-count">
                        {enabledCount} abilitati su {data?.employees.length ?? 0} dipendenti attivi
                    </span>
                    <Button variant="outline" onClick={onClose} data-testid="abilitazioni-close-btn">Chiudi</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function LiveTab() {
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        api.get("/presenze/live").then((r) => setData(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

    return (
        <div data-testid="presenze-live-tab">
            <div className="mb-4 grid grid-cols-3 gap-3">
                <Kpi label="In servizio ora" value={data.in_service_count} color="text-emerald-700" testid="live-kpi-inservice" />
                <Kpi label="Timbrature oggi" value={data.today_entries} color="text-slate-700" testid="live-kpi-today" />
                <Kpi label="Fuori assegnazione oggi" value={data.today_off_assignment} color="text-amber-700" testid="live-kpi-off" />
            </div>
            {data.sites.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="live-empty">
                    Nessun dipendente attualmente in servizio.
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {data.sites.map((s) => (
                        <div key={s.worksite_id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={`live-site-${s.worksite_id}`}>
                            <div className="mb-2 flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-[#376DB3]" />
                                <span className="font-display font-semibold text-slate-800">{s.worksite_name}</span>
                                <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{s.people.length}</span>
                            </div>
                            <ul className="divide-y divide-slate-100">
                                {s.people.map((p) => (
                                    <li key={p.employee_id} className="flex items-center justify-between py-1.5 text-sm">
                                        <Link to={`/dipendenti/${p.employee_id}`} className="font-medium text-slate-700 hover:text-[#376DB3] hover:underline">
                                            {p.name}
                                        </Link>
                                        <span className="flex items-center gap-2 text-xs text-slate-500">
                                            {!p.assigned && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">SOSTITUZIONE</span>}
                                            dalle <b className="font-mono-numbers">{fmtTime(p.since)}</b>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function EntriesTab({ worksites, employees }) {
    const [employeeId, setEmployeeId] = useState(ALL);
    const [worksiteId, setWorksiteId] = useState(ALL);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [flag, setFlag] = useState(ALL);
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [editEntry, setEditEntry] = useState(null);
    const [showManual, setShowManual] = useState(false);

    const params = useMemo(() => {
        const p = { page, page_size: 50 };
        if (employeeId !== ALL) p.employee_id = employeeId;
        if (worksiteId !== ALL) p.worksite_id = worksiteId;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        if (flag !== ALL) p.flag = flag;
        return p;
    }, [employeeId, worksiteId, dateFrom, dateTo, flag, page]);

    const load = useCallback(() => {
        api.get("/presenze/entries", { params }).then((r) => setData(r.data)).catch(() => toast.error("Errore nel caricamento"));
    }, [params]);

    useEffect(() => { load(); }, [load]);

    const remove = async (e) => {
        if (!window.confirm(`Eliminare la timbratura di ${e.employee_name} del ${fmtDateTime(e.at)}?`)) return;
        try {
            await api.delete(`/presenze/entries/${e.id}`);
            toast.success("Timbratura eliminata");
            load();
        } catch { toast.error("Errore nell'eliminazione"); }
    };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    return (
        <div data-testid="presenze-entries-tab">
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setPage(1); }}
                    className="h-9 w-[190px] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="entries-filter-employee">
                    <option value={ALL}>Tutti i dipendenti</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.cognome} {e.nome}</option>)}
                </select>
                <select value={worksiteId} onChange={(e) => { setWorksiteId(e.target.value); setPage(1); }}
                    className="h-9 w-[180px] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="entries-filter-worksite">
                    <option value={ALL}>Tutti i cantieri</option>
                    {worksites.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <select value={flag} onChange={(e) => { setFlag(e.target.value); setPage(1); }}
                    className="h-9 w-[190px] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="entries-filter-flag">
                    <option value={ALL}>Tutte le timbrature</option>
                    <option value="off_assignment">Fuori assegnazione</option>
                    <option value="manual">Inserite manualmente</option>
                    <option value="edited">Modificate</option>
                    <option value="shift_anomaly">Anomalie turno</option>
                    <option value="no_reason">Anomalie senza motivazione</option>
                </select>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Dal</span>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs" data-testid="entries-date-from" />
                    <span className="text-xs text-slate-400">al</span>
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs" data-testid="entries-date-to" />
                    <Button variant="ghost" size="icon" onClick={() => { setEmployeeId(ALL); setWorksiteId(ALL); setDateFrom(""); setDateTo(""); setFlag(ALL); setPage(1); }} title="Azzera filtri" data-testid="entries-reset-filters">
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                </div>
                <Button onClick={() => setShowManual(true)} className="ml-auto gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="entries-add-manual-btn">
                    <Plus className="h-4 w-4" /> Timbratura manuale
                </Button>
            </div>

            {!data ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : data.items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="entries-empty">
                    Nessuna timbratura trovata con i filtri selezionati.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <th className="px-3 py-2.5">Dipendente</th>
                                    <th className="px-3 py-2.5">Tipo</th>
                                    <th className="px-3 py-2.5">Data e ora</th>
                                    <th className="px-3 py-2.5">Cantiere</th>
                                    <th className="px-3 py-2.5">Posizione</th>
                                    <th className="px-3 py-2.5">Note</th>
                                    <th className="px-3 py-2.5 text-right">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((e) => (
                                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`entry-row-${e.id}`}>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <Link to={`/dipendenti/${e.employee_id}`} className="font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">
                                                {e.employee_name}
                                            </Link>
                                            {e.matricola && <div className="text-[11px] text-slate-400">Matr. {e.matricola}</div>}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${e.type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                                                {e.type === "in" ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
                                                {e.type === "in" ? "ENTRATA" : "USCITA"}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-700">{fmtDateTime(e.at)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">{e.worksite_name}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs">
                                            <div className="flex flex-wrap items-center gap-1">
                                                {e.manual ? (
                                                    <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700">MANUALE</span>
                                                ) : e.distance_m != null ? (
                                                    <span className="inline-flex items-center gap-1 text-slate-600">
                                                        <MapPin className="h-3 w-3 text-[#376DB3]" /> {e.distance_m} m
                                                    </span>
                                                ) : "—"}
                                                {e.assigned === false && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">FUORI ASSEGNAZIONE</span>}
                                                {e.corrected_by && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">MODIFICATA</span>}
                                                {e.shift_check && (
                                                    <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700" title={`Turno previsto: ${e.shift_check.expected} (${e.shift_check.activity_name})`} data-testid={`entry-shift-anomaly-${e.id}`}>
                                                        {SHIFT_ANOMALY_LABELS[e.shift_check.code] || e.shift_check.code} {e.shift_check.code === "uscita_anticipata" ? "−" : "+"}{e.shift_check.delta_min}′
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="max-w-[200px] px-3 py-2 text-xs text-slate-500">
                                            {e.reason ? (
                                                <span className="text-slate-700" title={e.reason} data-testid={`entry-reason-${e.id}`}>
                                                    <b>Motivazione:</b> {e.reason}
                                                </span>
                                            ) : e.requires_reason ? (
                                                <span className="font-semibold text-amber-600" data-testid={`entry-noreason-${e.id}`}>Senza motivazione</span>
                                            ) : (e.note || "—")}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditEntry(e)} title="Modifica" data-testid={`entry-edit-${e.id}`}>
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(e)} title="Elimina" data-testid={`entry-delete-${e.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span data-testid="entries-pagination-info">{data.total.toLocaleString("it-IT")} timbrature</span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="entries-prev-page">
                                <ChevronLeft className="h-4 w-4" /> Prec.
                            </Button>
                            <span className="font-mono-numbers">pag. {data.page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="entries-next-page">
                                Succ. <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <EntryDialog
                entry={editEntry}
                manual={showManual}
                worksites={worksites}
                employees={employees}
                onClose={() => { setEditEntry(null); setShowManual(false); }}
                onSaved={() => { setEditEntry(null); setShowManual(false); load(); }}
            />
        </div>
    );
}

function EntryDialog({ entry, manual, worksites, employees, onClose, onSaved }) {
    const open = !!entry || manual;
    const [employeeId, setEmployeeId] = useState("");
    const [worksiteId, setWorksiteId] = useState("");
    const [type, setType] = useState("in");
    const [at, setAt] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setEmployeeId(entry?.employee_id || "");
            setWorksiteId(entry?.worksite_id || "");
            setType(entry?.type || "in");
            setAt(entry ? toLocalInput(entry.at) : "");
            setNote(entry?.note || "");
        }
    }, [open, entry]);

    const save = async () => {
        setSaving(true);
        try {
            if (entry) {
                await api.patch(`/presenze/entries/${entry.id}`, { at, type, worksite_id: worksiteId, note });
                toast.success("Timbratura aggiornata");
            } else {
                if (!employeeId || !worksiteId || !at) {
                    toast.error("Compila dipendente, cantiere e data/ora");
                    setSaving(false);
                    return;
                }
                await api.post("/presenze/entries", { employee_id: employeeId, worksite_id: worksiteId, type, at, note });
                toast.success("Timbratura manuale registrata");
            }
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="entry-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">{entry ? "Modifica timbratura" : "Timbratura manuale"}</DialogTitle>
                    <DialogDescription>
                        {entry ? `${entry.employee_name} — la modifica resta tracciata` : "Per casi eccezionali (es. dipendente senza smartphone). Resta marcata come manuale."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {!entry && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dipendente *</Label>
                            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="entry-employee-select">
                                <option value="">Seleziona…</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.cognome} {e.nome}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Tipo *</Label>
                            <select value={type} onChange={(e) => setType(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="entry-type-select">
                                <option value="in">Entrata</option>
                                <option value="out">Uscita</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Data e ora *</Label>
                            <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="entry-at-input" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Cantiere *</Label>
                        <select value={worksiteId} onChange={(e) => setWorksiteId(e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="entry-worksite-select">
                            <option value="">Seleziona…</option>
                            {worksites.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Nota</Label>
                        <Input className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. telefono scarico, inserita su richiesta" data-testid="entry-note-input" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="entry-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TimesheetTab() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailEmp, setDetailEmp] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get("/presenze/timesheet", { params: { year, month } })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore nel caricamento del foglio ore"))
            .finally(() => setLoading(false));
    }, [year, month]);

    const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
    const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

    const exportXlsx = async () => {
        setExporting(true);
        try {
            const { data: blob } = await api.get("/presenze/timesheet-export.xlsx", {
                params: { year, month }, responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([blob]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `foglio-ore-${year}-${String(month).padStart(2, "0")}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Foglio ore esportato");
        } catch { toast.error("Errore nell'esportazione"); } finally { setExporting(false); }
    };

    return (
        <div data-testid="presenze-timesheet-tab">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={prevMonth} data-testid="timesheet-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="w-44 text-center font-display font-semibold text-slate-800" data-testid="timesheet-month-label">{MONTHS[month - 1]} {year}</span>
                    <Button variant="outline" size="icon" onClick={nextMonth} data-testid="timesheet-next-month"><ChevronRight className="h-4 w-4" /></Button>
                </div>
                <Button onClick={exportXlsx} disabled={exporting || !data?.rows?.length} className="gap-2 bg-[#15803D] text-white hover:bg-[#116230]" data-testid="timesheet-export-btn">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                    Esporta Excel
                </Button>
            </div>

            {loading || !data ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : data.rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="timesheet-empty">
                    Nessuna timbratura registrata in {MONTHS[month - 1]} {year}.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-2.5">Dipendente</th>
                                <th className="px-3 py-2.5 text-right">Ore lavorate</th>
                                <th className="px-3 py-2.5 text-right">Ore contrattuali (media mese)</th>
                                <th className="px-3 py-2.5 text-right">Differenza</th>
                                <th className="px-3 py-2.5 text-right">Giorni</th>
                                <th className="px-3 py-2.5">Anomalie</th>
                                <th className="px-3 py-2.5 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.employee_id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`timesheet-row-${r.employee_id}`}>
                                    <td className="whitespace-nowrap px-3 py-2">
                                        <Link to={`/dipendenti/${r.employee_id}`} className="font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">{r.name}</Link>
                                        {r.matricola && <span className="ml-2 text-[11px] text-slate-400">matr. {r.matricola}</span>}
                                        {r.in_progress && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">IN SERVIZIO</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-numbers font-bold text-slate-800">{fmtOre(r.ore_lavorate)}</td>
                                    <td className="px-3 py-2 text-right font-mono-numbers text-slate-500">{fmtOre(r.ore_contrattuali)}</td>
                                    <td className={`px-3 py-2 text-right font-mono-numbers font-semibold ${r.diff == null ? "text-slate-400" : r.diff >= 0 ? "text-emerald-700" : "text-amber-700"}`}>
                                        {r.diff != null ? (r.diff >= 0 ? "+" : "") + fmtOre(r.diff) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-numbers text-slate-600">{r.giorni}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex flex-wrap gap-1">
                                            {r.anomalies.missing_out > 0 && (
                                                <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
                                                    <AlertTriangle className="h-3 w-3" /> {r.anomalies.missing_out} uscite mancanti
                                                </span>
                                            )}
                                            {r.anomalies.off_assignment > 0 && (
                                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                                    {r.anomalies.off_assignment} fuori assegnazione
                                                </span>
                                            )}
                                            {r.anomalies.missing_out === 0 && r.anomalies.off_assignment === 0 && <span className="text-xs text-slate-300">—</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <Button variant="outline" size="sm" onClick={() => setDetailEmp(r)} data-testid={`timesheet-detail-${r.employee_id}`}>Dettaglio</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DayDetailDialog emp={detailEmp} year={year} month={month} onClose={() => setDetailEmp(null)} />
        </div>
    );
}

const FLAG_LABELS = {
    uscita_mancante: { label: "Uscita mancante", cls: "bg-red-100 text-red-600" },
    entrata_mancante: { label: "Entrata mancante", cls: "bg-red-100 text-red-600" },
    fuori_assegnazione: { label: "Fuori assegnazione", cls: "bg-amber-100 text-amber-700" },
    in_corso: { label: "In corso", cls: "bg-emerald-100 text-emerald-700" },
};

function DayDetailDialog({ emp, year, month, onClose }) {
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        if (emp) {
            setDetail(null);
            api.get(`/presenze/timesheet/${emp.employee_id}`, { params: { year, month } })
                .then((r) => setDetail(r.data))
                .catch(() => toast.error("Errore nel caricamento del dettaglio"));
        }
    }, [emp, year, month]);

    return (
        <Dialog open={!!emp} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto" data-testid="timesheet-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">{emp?.name} — {MONTHS[month - 1]} {year}</DialogTitle>
                    <DialogDescription>Dettaglio giornaliero delle timbrature e ore calcolate</DialogDescription>
                </DialogHeader>
                {!detail ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {Object.entries(detail.days || {}).map(([day, d]) => (
                            <div key={day} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                                <span className="w-24 font-mono-numbers font-semibold text-slate-700">
                                    {new Date(day + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
                                    <span className="ml-1 text-[10px] uppercase text-slate-400">
                                        {new Date(day + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short" })}
                                    </span>
                                </span>
                                <span className="flex flex-1 flex-wrap gap-1.5">
                                    {d.entries.map((e) => (
                                        <span key={e.id} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono-numbers text-[11px] font-semibold ${e.type === "in" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                                            {e.type === "in" ? "E" : "U"} {fmtTime(e.at)}
                                            {e.manual && <span className="text-[9px]">(man.)</span>}
                                        </span>
                                    ))}
                                </span>
                                <span className="flex gap-1">
                                    {d.flags.map((f) => (
                                        <span key={f} className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${FLAG_LABELS[f]?.cls || "bg-slate-100 text-slate-600"}`}>
                                            {FLAG_LABELS[f]?.label || f}
                                        </span>
                                    ))}
                                </span>
                                <span className="w-14 text-right font-mono-numbers font-bold text-slate-800">{fmtOre(d.ore)}</span>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-3 text-sm font-bold text-slate-800">
                            <span>Totale mese</span>
                            <span className="font-mono-numbers">{fmtOre(detail.ore_lavorate)}</span>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Kpi({ label, value, color, testid }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testid}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
            <div className={`mt-1 font-mono-numbers text-2xl font-bold ${color}`}>{value}</div>
        </div>
    );
}
